import { useMemo, useState } from "react";
import { articles, blackRegions, timeRanges } from "@/mocks/articles";
import { usePageMeta } from "@/hooks/usePageMeta";
import { formatViews, withinDays } from "@/utils/format";
import { Link } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FilterPanel, { type FilterGroup } from "@/components/feature/FilterPanel";
import ArticleCard from "@/components/feature/ArticleCard";
import Pagination from "@/components/feature/Pagination";

const PAGE_SIZE = 6;

const filterGroups: FilterGroup[] = [
  {
    key: "region",
    label: "地区",
    options: blackRegions.map((region) => ({ label: region, value: region })),
  },
  {
    key: "time",
    label: "发布时间",
    options: timeRanges.map((range) => ({ label: range.label, value: range.value })),
  },
];

const blackArticles = articles.filter((item) => item.category === "black");
const blackHot = [...blackArticles].sort((a, b) => b.views - a.views).slice(0, 4);

export default function BlackPage() {
  usePageMeta({
    title: "明星黑料大全 - 黑料吃瓜",
    description:
      "黑料吃瓜黑料频道收录明星黑料与行业深度揭秘，覆盖大陆、港台、欧美、日韩地区，按发布时间筛选，附热度与相关标签。",
    keywords: "明星黑料,黑料大全,深度爆料,娱乐圈内幕,片酬,合约纠纷,黑料吃瓜",
  });

  const [filters, setFilters] = useState<Record<string, string>>({ region: "全部", time: "all" });
  const [page, setPage] = useState(1);

  const filtered = useMemo(
    () =>
      blackArticles
        .filter(
          (item) =>
            (filters.region === "全部" || item.region === filters.region) && withinDays(item.date, filters.time),
        )
        .sort((a, b) => (a.date < b.date ? 1 : -1)),
    [filters],
  );

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const handleFilter = (key: string, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const summary = [
    filters.region !== "全部" ? filters.region : "",
    filters.time !== "all" ? `近 ${filters.time} 天` : "",
  ]
    .filter(Boolean)
    .join(" · ") || "全部内容";

  return (
    <div className="max-w-[1320px] mx-auto px-4 md:px-6 py-5 md:py-7">
      <Breadcrumb items={[{ label: "黑料频道" }]} />

      <header className="mt-3 mb-5 md:mb-6">
        <h1 className="font-heading text-2xl md:text-[30px] font-black text-foreground-950">明星黑料大全</h1>
        <p className="text-[13px] md:text-sm text-foreground-500 leading-7 mt-2 max-w-3xl">
          收录片酬争议、合约纠纷、代言翻车等深度爆料内容。所有条目均附事件脉络与多方回应，热度数据仅代表站内阅读与互动情况。
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[256px_minmax(0,1fr)] gap-5 md:gap-6">
        <aside>
          <FilterPanel groups={filterGroups} values={filters} onChange={handleFilter} summary={summary} />

          <div className="hidden lg:block mt-4 rounded-lg border border-background-200 bg-background-50 overflow-hidden">
            <div className="px-4 py-3 border-b border-background-200 bg-background-100">
              <h3 className="font-heading text-[14px] font-bold text-foreground-900 flex items-center gap-2">
                <i className="ri-fire-line text-accent-500" />
                黑料热榜
              </h3>
            </div>
            <ol className="divide-y divide-background-200">
              {blackHot.map((article, index) => (
                <li key={article.id}>
                  <Link
                    to={`/detail/${article.id}`}
                    className="group flex items-start gap-2.5 px-4 py-3 hover:bg-background-100 transition-colors cursor-pointer"
                  >
                    <span
                      className={`w-4 h-4 shrink-0 rounded text-[10px] font-bold flex items-center justify-center mt-1 ${
                        index < 3 ? "bg-accent-500 text-primary-950" : "bg-background-200 text-foreground-600"
                      }`}
                    >
                      {index + 1}
                    </span>
                    <span className="text-[12.5px] text-foreground-700 leading-6 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {article.title}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          </div>
        </aside>

        <section>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3.5">
            <p className="text-[13px] text-foreground-600">
              共 <span className="text-accent-600 font-bold">{filtered.length}</span> 条深度爆料
              <span className="hidden sm:inline text-foreground-400"> · 第 {currentPage} / {totalPages} 页</span>
            </p>
            <span className="inline-flex items-center gap-1.5 text-[12px] text-foreground-500">
              <i className="ri-information-line" />
              热度 = 站内阅读 + 互动
            </span>
          </div>

          {visible.length > 0 ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {visible.map((article) => (
                  <Link
                    key={article.id}
                    to={`/detail/${article.id}`}
                    className="group flex flex-col rounded-lg overflow-hidden border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="relative w-full h-44 md:h-48 overflow-hidden bg-background-200">
                      <img
                        src={article.cover}
                        alt={article.title}
                        title={`${article.title} - 黑料吃瓜`}
                        className="w-full h-full object-cover object-top group-hover:scale-[1.04] transition-transform duration-500"
                      />
                      <span className="absolute top-2.5 left-2.5 inline-flex items-center gap-1 px-2 py-1 rounded bg-primary-950/85 text-background-50 text-[11px] font-medium backdrop-blur-sm">
                        <i className="ri-file-shield-2-line text-accent-400" />
                        深度爆料
                      </span>
                      {article.region && (
                        <span className="absolute top-2.5 right-2.5 px-2 py-1 rounded bg-background-50/90 text-[11px] text-foreground-700">
                          {article.region}
                        </span>
                      )}
                    </div>
                    <div className="p-4 flex flex-col flex-1">
                      <h2 className="font-heading text-[15.5px] font-bold text-foreground-900 leading-snug line-clamp-2 group-hover:text-primary-600 transition-colors">
                        {article.title}
                      </h2>
                      <p className="text-[12.5px] text-foreground-500 leading-6 mt-1.5 line-clamp-2">
                        {article.excerpt}
                      </p>
                      <div className="flex flex-wrap gap-1.5 mt-2.5">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-1.5 py-0.5 rounded bg-secondary-100 text-secondary-800 text-[11px]"
                          >
                            #{tag}
                          </span>
                        ))}
                      </div>
                      <div className="mt-auto pt-3 flex items-center justify-between text-[11.5px] text-foreground-400">
                        <span>{article.date}</span>
                        <span className="flex items-center gap-3">
                          <span className="flex items-center gap-1">
                            <i className="ri-eye-line" />
                            {formatViews(article.views)}
                          </span>
                          <span className="flex items-center gap-1">
                            <i className="ri-heart-3-line" />
                            {formatViews(article.likes)}
                          </span>
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <Pagination current={currentPage} total={totalPages} onChange={setPage} />
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <i className="ri-file-search-line text-3xl text-foreground-300" />
              <p className="font-heading text-base font-bold text-foreground-800 mt-3">该地区暂无可展示的爆料</p>
              <p className="text-[13px] text-foreground-500 mt-1.5">换一个地区或时间范围试试看。</p>
              <button
                type="button"
                onClick={() => {
                  setFilters({ region: "全部", time: "all" });
                  setPage(1);
                }}
                className="inline-flex items-center gap-1.5 mt-4 px-4 h-9 rounded-md bg-primary-500 text-background-50 text-[13px] whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
              >
                <i className="ri-refresh-line" />
                重置筛选
              </button>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}